import {
  type BugReportScreenshot,
  BugReportScreenshotError,
  MAX_BUG_REPORT_SCREENSHOTS,
  prepareBugReportScreenshot,
  type ScreenshotErrorCode,
} from 'features/knowledgeDocumentViewer/utils/bugReportScreenshots'
import { useCallback, useEffect, useRef, useState } from 'react'

export function useBugReportScreenshots() {
  const [screenshots, setScreenshots] = useState<BugReportScreenshot[]>([])
  const [operation, setOperation] = useState<'files' | null>(null)
  const [error, setError] = useState<ScreenshotErrorCode | null>(null)
  const generation = useRef(0)
  const busy = useRef(false)

  useEffect(
    () => () => {
      generation.current += 1
    },
    []
  )

  const cancelOperation = useCallback(() => {
    generation.current += 1
    busy.current = false
    setOperation(null)
  }, [])

  const reset = useCallback(() => {
    cancelOperation()
    setScreenshots([])
    setError(null)
  }, [cancelOperation])

  const addFiles = useCallback(
    async (files: File[]) => {
      if (busy.current || files.length === 0) return
      if (screenshots.length + files.length > MAX_BUG_REPORT_SCREENSHOTS) {
        setError('limit')
        return
      }
      const current = ++generation.current
      busy.current = true
      setOperation('files')
      setError(null)
      try {
        const additions = await Promise.all(
          files.map((file) => prepareBugReportScreenshot(file))
        )
        if (generation.current === current) {
          setScreenshots((previous) => [...previous, ...additions])
        }
      } catch (failure) {
        if (generation.current === current) {
          setError(
            failure instanceof BugReportScreenshotError ? failure.code : 'read'
          )
        }
      } finally {
        if (generation.current === current) {
          busy.current = false
          setOperation(null)
        }
      }
    },
    [screenshots.length]
  )

  const remove = useCallback((id: string) => {
    setScreenshots((current) =>
      current.filter((screenshot) => screenshot.id !== id)
    )
    setError(null)
  }, [])

  return {
    screenshots,
    operation,
    error,
    addFiles,
    remove,
    reset,
  }
}
